import { toTimestamp } from '../utils/addWebSocketToReplication'
import { createReplication } from './createReplication'
import type { PullRequestCollection } from '../index'

/**
 * Pull-only replication for linked pull requests. The client never writes these — the only
 * writers are the Super Agent relay and the GitHub webhook (server-side) — so `mapToServer`
 * is deliberately omitted, which disables push in the createReplication factory.
 */
export const setupPullRequestReplication = (
  collection: PullRequestCollection,
  envId: string,
) =>
  createReplication({
    entityName: 'workitempullrequests',
    displayName: 'PullRequest',
    collection,
    envId,
    mapFromServer: (doc) => ({
      id: doc.id,
      envId: doc.envId,
      taskId: doc.workItemId || doc.taskId,
      provider: doc.provider,
      repositoryFullName: doc.repositoryFullName,
      number: doc.number,
      providerPullRequestId: doc.providerPullRequestId ?? null,
      url: doc.url ?? null,
      htmlUrl: doc.htmlUrl ?? null,
      sourceBranch: doc.sourceBranch ?? null,
      targetBranch: doc.targetBranch ?? null,
      headRepoFullName: doc.headRepoFullName ?? null,
      headSha: doc.headSha ?? null,
      state: doc.state || 'open',
      ciStatus: doc.ciStatus || 'unknown',
      reviewStatus: doc.reviewStatus || 'none',
      mergeability: doc.mergeability || 'unknown',
      mergedOn: doc.mergedOn ? (toTimestamp(doc.mergedOn) ?? new Date(doc.mergedOn).getTime()) : null,
      // Fall back to updatedOn if providerUpdatedAt is missing/unparseable, so the row never sorts
      // as epoch-0 (which would sink a live PR to the bottom of the list).
      providerUpdatedAt: doc.providerUpdatedAt
        ? (toTimestamp(doc.providerUpdatedAt) ?? new Date(doc.providerUpdatedAt).getTime())
        : (toTimestamp(doc.updatedOn) ?? new Date(doc.updatedOn).getTime()),
      lastObservedSource: doc.lastObservedSource ?? null,
      authorGitHubLogin: doc.authorGitHubLogin ?? null,
      isDeleted: doc.isDeleted || false,
      createdOn: toTimestamp(doc.createdOn) ?? new Date(doc.createdOn).getTime(),
      createdBy: doc.createdBy,
      updatedOn: toTimestamp(doc.updatedOn) ?? new Date(doc.updatedOn).getTime(),
      updatedBy: doc.updatedBy,
      deletedOn: doc.deletedOn ? (toTimestamp(doc.deletedOn) ?? new Date(doc.deletedOn).getTime()) : null,
      deletedBy: doc.deletedBy || null,
    }),
  })
